import { InjectionToken, makeStateKey } from '@angular/core';
import { JobCatsOne, Recruiter } from './vacancy-data.component';

/**
 * Contract of the BFF `GET /vacancy-documents/resolve?path=` endpoint. The server renderer and the
 * browser both classify a vacancy URL through it, so a list, a job, a redirect, a removed job (410)
 * and an unknown job (404) are decided in one place and render identically on both sides.
 */
export const VACANCY_DOCUMENT_CONTRACT_VERSION = 1;

/** A listed vacancy: the job without its full description. */
export type VacancySummary = Omit<JobCatsOne, 'description' | 'recruiter' | 'directMatch'>;

export interface VacancyDetail extends JobCatsOne {
  recruiter?: Recruiter;
}

export type VacancyResolution =
  | { kind: 'list'; path: string; jobs: VacancySummary[] }
  | { kind: 'job'; path: string; job: VacancyDetail; related: VacancySummary[] }
  | { kind: 'redirect'; path: string }
  | { kind: 'gone'; path: string; title: string | null; related: VacancySummary[] }
  | { kind: 'not-found'; path: string; related: VacancySummary[] }
  | { kind: 'unavailable'; path: string };

/** HTTP status the renderer answers with for each outcome that renders a page. */
export function documentStatusFor(resolution: VacancyResolution): number {
  switch (resolution.kind) {
    case 'gone':
      return 410;
    case 'not-found':
      return 404;
    default:
      return 200;
  }
}

/** The document paths the resolve endpoint accepts; everything else is not a vacancy document. */
const LIST_PATH = '/vacatures';
const JOB_SEGMENT = /^(?:[a-z0-9]+(?:-[a-z0-9]+)*-)?[0-9]{1,10}$/;
const LEGACY_JOB_PATH = /^\/vacatures\/bekijk-vacature\/[0-9]{1,10}$/;

export function isVacancyDocumentPath(path: string): boolean {
  if (path === LIST_PATH || LEGACY_JOB_PATH.test(path)) return true;
  const match = /^\/vacatures\/([^/]+)$/.exec(path);
  return !!match && JOB_SEGMENT.test(match[1]);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function summaries(value: unknown): VacancySummary[] {
  return Array.isArray(value) ? value.filter((job): job is VacancySummary => isObject(job) && typeof job['id'] === 'number') : [];
}

/**
 * Interprets a resolve response. Only contract-shaped bodies count: anything else (an older API
 * without the route, HTML from a proxy, a malformed body) is `unavailable`, never "missing".
 */
export function interpretVacancyResolve(status: number, body: unknown, requestPath: string): VacancyResolution {
  const unavailable: VacancyResolution = { kind: 'unavailable', path: requestPath };
  if (!isObject(body) || body['contractVersion'] !== VACANCY_DOCUMENT_CONTRACT_VERSION) return unavailable;
  const code = isObject(body['error']) ? body['error']['code'] : undefined;
  if (status === 410 && code === 'VACANCY_GONE') {
    return { kind: 'gone', path: requestPath, title: typeof body['title'] === 'string' ? body['title'] : null, related: summaries(body['related']) };
  }
  if ((status === 404 && code === 'VACANCY_NOT_FOUND') || (status === 400 && code === 'VACANCY_PATH_INVALID')) {
    return { kind: 'not-found', path: requestPath, related: summaries(body['related']) };
  }
  if (status !== 200) return unavailable;
  if (body['kind'] === 'list' && body['path'] === requestPath && Array.isArray(body['jobs'])) {
    return { kind: 'list', path: requestPath, jobs: summaries(body['jobs']) };
  }
  if (body['kind'] === 'job' && body['path'] === requestPath && isObject(body['job']) && typeof body['job']['id'] === 'number') {
    return { kind: 'job', path: requestPath, job: body['job'] as unknown as VacancyDetail, related: summaries(body['related']) };
  }
  if (body['kind'] === 'redirect' && body['statusCode'] === 301) {
    const target = body['path'];
    if (typeof target === 'string' && target !== requestPath && isVacancyDocumentPath(target)) return { kind: 'redirect', path: target };
  }
  return unavailable;
}

/**
 * Request-scoped resolution the server renderer provides per render (never a module singleton).
 * It is recorded in TransferState so the browser renders the same data without a second request.
 */
export const VACANCY_RENDER_PAYLOAD = new InjectionToken<VacancyResolution>('VACANCY_RENDER_PAYLOAD');
export const VACANCY_TRANSFER_KEY = makeStateKey<VacancyResolution>('vacancy.document');
